<template>
    <div
        class="filter-menu"
        v-if="this.openMenu"
        v-bind:style="{
            transform: currentY ? `translateY(${currentY}px)` : undefined,
            transition: currentY ? 'none' : undefined,
        }"
    >
        <div class="card filter-menu__card">
            <div class="filter-menu__grabber-container"
                v-on:touchstart="handleTouchStart"
                v-on:touchmove="handleTouchMove"
                v-on:touchend="handleTouchEnd"
            >
                <div class="filter-menu__grabber"></div>
            </div>
            <div class="filter-section">
                <div class="filter-section__title">
                    <span>{{$t('filter.actionTypes')}}</span>
                    <button type="button" v-show="excludeGroups.length > 0" v-on:click="resetGroups" class="filter-section__reset">
                        {{ $t('common.reset') }}
                    </button>
                    <button type="button" v-show="excludeGroups.length === 0" v-on:click="disableAllGroups" class="filter-section__reset">
                        {{ $t('common.disable_all') }}
                    </button>
                </div>
                <div class="filter-chips">
                    <button v-for="(_, group) in shortActionGroupsList"
                        type="button"
                        class="filter-chips__chip"
                        v-bind:class="{'filter-chips__chip--active': !excludeGroups.includes(group)}"
                        v-bind:aria-pressed="String(!excludeGroups.includes(group))"
                        v-on:click="toggleGroup(group)"
                        v-bind:key="group"
                    >
                        {{ $t(`filter.actionGroups.${group}`) }}
                    </button>
                    <button v-if="!openActionGroup"
                        type="button"
                        class="filter-chips__chip filter-chips__chip--more"
                        v-on:click="openActionGroup = true"
                    >
                        +{{ Object.keys(actionGroups).length - 5 }}
                    </button>
                </div>
            </div>
            <div v-if="IS_TESTNET" class="filter-section">
                <div class="filter-section__title">
                    <span>{{$t('filter.direction')}}</span>
                </div>
                <div class="filter-options" role="radiogroup">
                    <button v-for="option in directionOptions"
                        type="button"
                        role="radio"
                        class="filter-options__option"
                        v-bind:class="{ 'filter-options__option--active': option.value === transactionDirection }"
                        v-bind:aria-checked="String(option.value === transactionDirection)"
                        v-bind:key="option.value"
                        v-on:click="setTxDirection(option.value)"
                    >
                        <span class="filter-options__title">{{ option.label }}</span>
                        <span v-if="option.description" class="filter-options__description">{{ option.description }}</span>
                    </button>
                </div>
            </div>
            <div class="filter-section">
                <div class="filter-section__title">
                    <span>{{$t('filter.timeFormat')}}</span>
                </div>
                <div class="filter-options" role="radiogroup">
                    <button v-for="option in timeFormatOptions"
                        type="button"
                        role="radio"
                        class="filter-options__option"
                        v-bind:class="{ 'filter-options__option--active': option.value === (timeFormatData === 'unix' ? 'absolute' : timeFormatData) }"
                        v-bind:aria-checked="String(option.value === (timeFormatData === 'unix' ? 'absolute' : timeFormatData))"
                        v-bind:key="option.value"
                        v-on:click="setTimeFormatData(option.value)"
                    >
                        <span class="filter-options__title">{{ option.label }}</span>
                        <span v-if="option.description" class="filter-options__description">{{ option.description }}</span>
                    </button>
                </div>
                <button v-if="timeFormatData === 'unix' || timeFormatData === 'absolute'"
                    type="button"
                    class="filter-switch-row filter-switch-row--nested"
                    v-on:click="toggleTimeFormat"
                >
                    <span class="filter-switch-row__title">{{$t('filter.switchToUnixTime')}}</span>
                    <span class="filter-switch" v-bind:class="{ 'filter-switch--on': timeFormatData === 'unix' }"></span>
                </button>
            </div>
            <div class="filter-section filter-switch-group">
                <!-- Only the desktop «simple» table has the column: mobile rows always show comments. -->
                <button v-if="!isMobile && transactionViewData !== 'professional'"
                    type="button"
                    class="filter-switch-row"
                    v-on:click="toggleTxComments"
                >
                    <span class="filter-switch-row__title">{{$t('filter.txComments')}}</span>
                    <span class="filter-switch" v-bind:class="{ 'filter-switch--on': showTxComments }"></span>
                </button>
                <button type="button" class="filter-switch-row" v-on:click="toggleInitiatedByAccount">
                    <span class="filter-switch-row__title">{{$t('filter.initiatedByAccount')}}</span>
                    <span class="filter-switch" v-bind:class="{ 'filter-switch--on': initiatedByAccount }"></span>
                </button>
                <!-- «Raw transactions» is the Developer list view (transactionViewData = professional): -->
                <button type="button" class="filter-switch-row" v-on:click="toggleRawTransactions">
                    <span class="filter-switch-row__title">{{$t('filter.rawTransactions')}}</span>
                    <span class="filter-switch" v-bind:class="{ 'filter-switch--on': transactionViewData === 'professional' }"></span>
                </button>
            </div>
            <button type="button" class="filter-close-btn" v-on:click="handleClick">
                {{ $t('common.close') }}
            </button>
        </div>
    </div>
</template>

<script>
import { IS_TESTNET } from '~/config';
import { mapState } from 'vuex';

export default {
    data() {
        return {
            actionGroups: this.$store.state.actionGroups,
            openActionGroup: false,
            timeFormatData: this.$store.state.timeFormatData,
            transactionViewData: this.$store.state.transactionViewData,
            startY: 0,
            currentY: 0,
        };
    },
    props: ['toggleMenu', 'openMenu'],
    computed: {
        IS_TESTNET() {
            return IS_TESTNET;
        },
        shortActionGroupsList() {
            if (this.openActionGroup) return this.actionGroups;
            return Object.fromEntries(
                Object.entries(this.actionGroups).slice(0, 5),
            );
        },
        timeFormatOptions() {
            return [
                { value: 'relative', label: this.$t('filter.timeRelativeTitle'), description: this.$t('filter.timeRelativeDescription') },
                { value: 'smart', label: this.$t('filter.timeSmartTitle'), description: this.$t('filter.timeSmartDescription') },
                { value: 'absolute', label: this.$t('filter.timeAbsoluteTitle'), description: this.$t('filter.timeAbsoluteDescription') },
            ];
        },
        directionOptions() {
            return [
                { value: 'all', label: this.$t('filter.allTx') },
                { value: 'sender', label: this.$t('filter.sentTx') },
                { value: 'receiver', label: this.$t('filter.receivedTx') },
            ];
        },
        initiatedByAccount() {
            return this.$store.getters.initiatedByAccountFor(this.$route.params.address);
        },
        ...mapState(['excludeGroups', 'transactionDirection', 'showTxComments']),
    },
    watch: {
        // The reset cross on the filter button can switch the view back while the menu is open:
        '$store.state.transactionViewData': function (value) {
            this.transactionViewData = value;
        },
    },
    methods: {
        handleTouchStart(event) {
            this.startY = event.touches[0].clientY;
            this.currentY = 0;
        },
        handleTouchMove(event) {
            const touchY = event.touches[0].clientY;
            const deltaY = touchY - this.startY;
            if (deltaY > 0) {
                this.currentY = deltaY;
            } else {
                this.currentY = 0;
            }
        },
        handleTouchEnd() {
            if (this.currentY >= 140) {
                this.toggleMenu();
            }
            this.currentY = 0;
        },
        resetGroups() {
            this.$store.commit('resetExcludeActionGroup');
        },
        disableAllGroups() {
            this.$store.commit('disableAllExcludeActionGroup');
        },
        toggleGroup(group) {
            this.$store.commit('toggleExcludeActionGroup', group);
        },
        toggleTimeFormat() {
            const currentFormat = this.$store.state.timeFormatData === 'unix' ? 'absolute' : 'unix';
            this.$store.commit('updateTimeFormatData', currentFormat);
            window.localStorage.setItem('timeFormatData', currentFormat);
            this.timeFormatData = currentFormat;
        },
        handleClick() {
            this.toggleMenu();
        },
        setTimeFormatData(value) {
            this.timeFormatData = value;
            this.$store.commit('updateTimeFormatData', value);
            window.localStorage.setItem('timeFormatData', value);
        },
        toggleRawTransactions() {
            this.setTransactionViewData(this.transactionViewData === 'professional' ? 'simple' : 'professional');
        },
        setTransactionViewData(value) {
            this.transactionViewData = value;
            this.$store.commit('updateTransactionViewData', value);
            window.localStorage.setItem('transactionViewData', value);
        },
        toggleTxComments() {
            this.$store.commit('switchShowTxComments');
        },
        toggleInitiatedByAccount() {
            this.$store.commit('updateInitiatedByAccount', {
                address: this.$route.params.address,
                value: !this.initiatedByAccount,
            });
        },
        setTxDirection(value) {
            this.$store.commit('updateTransactionDirection', value);
        },
    },
};
</script>

<style lang="scss">
// Same card, spacing and controls as the date picker next to it (CalendarMenu.vue):
.filter-menu {
    position: absolute;
    top: calc(100% + 8px);
    right: 10px;
    width: 360px;
    z-index: 90001;
    transform-origin: top right;
    animation: filter-menu-in .16s cubic-bezier(.2, .8, .2, 1);

    &__card {
        box-sizing: border-box;
        padding: 14px 14px 16px;
        user-select: none;
        box-shadow: 0 1px 2px var(--card-box-shadow-color),
                    0 16px 40px -12px rgba(0, 0, 0, .35);
    }
    &__grabber-container {
        display: none;
        justify-content: center;
        align-items: center;
        height: 20px;
        margin: -6px 0 4px;
        touch-action: none;
    }
    &__grabber {
        width: 36px;
        height: 5px;
        border-radius: 3px;
        background-color: var(--card-border-color);
    }
}

.filter-section {
    & + & {
        margin-top: 16px;
    }

    &__title {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 12px;
        min-height: 20px;
        margin: 0 2px 8px;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: -0.01em;
        color: var(--body-text-color);
    }
    &__reset {
        flex-shrink: 0;
        margin: -4px -6px;
        padding: 4px 6px;
        border: none;
        border-radius: 6px;
        background: transparent;
        color: var(--blue-bright);
        font: inherit;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;

        &:hover {
            background: var(--blue-bright-background);
        }
    }
}

.filter-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    // Same dark tray as the option groups below, so the chips read as toggles:
    padding: 8px;
    border-radius: 12px;
    background: var(--modal-dark-background);

    &__chip {
        height: 30px;
        padding: 0 11px;
        border: 1px solid var(--card-border-color);
        border-radius: 999px;
        background: transparent;
        color: var(--body-muted-text-color);
        font: inherit;
        font-size: 13px;
        font-weight: 500;
        white-space: nowrap;
        cursor: pointer;
        transition: color .15s ease, background-color .15s ease, border-color .15s ease, transform .1s ease;

        &:hover {
            color: var(--body-text-color);
            background: var(--icon-button-hover-background);
        }
        &:active {
            transform: scale(.96);
        }
        &--active, &--active:hover {
            border-color: transparent;
            background: var(--blue-bright-background);
            color: var(--blue-bright);
        }
        &--more, &--more:hover {
            border-color: transparent;
            background: var(--body-light-muted-color);
            color: var(--body-text-color);
        }
    }
}

// Options with a title and an example line under it; the picked one gets a blue frame:
.filter-options {
    position: relative;
    display: flex;
    border-radius: 12px;
    background: var(--modal-dark-background);

    &__option {
        position: relative;
        // Wider titles («Относительный») take more room than short ones:
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 5px;
        min-height: 44px;
        padding: 8px 4px;
        border: 2px solid transparent;
        border-radius: 12px;
        background: transparent;
        color: var(--body-text-color);
        font: inherit;
        text-align: center;
        cursor: pointer;
        transition: border-color .15s ease, background-color .15s ease;

        // Dividers between options, hidden next to the picked one:
        & + &::before {
            content: "";
            position: absolute;
            left: -2px;
            top: 8px;
            bottom: 8px;
            width: 1px;
            background: var(--card-row-separator);
        }
        &--active {
            border-color: var(--blue-bright);
            background: var(--modal-button-select-color);
        }
        &--active::before,
        &--active + &::before {
            opacity: 0;
        }
    }
    &__title {
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
    }
    &__description {
        font-size: 12px;
        line-height: 1.3;
        color: var(--body-muted-text-color);
    }
}

.filter-switch-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    min-height: 44px;
    padding: 0 12px 0 14px;
    border: none;
    border-radius: 12px;
    background: var(--body-light-muted-color);
    color: var(--body-text-color);
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    text-align: left;
    cursor: pointer;

    &--nested {
        min-height: 40px;
        margin-top: 8px;
        font-size: 13px;
    }
}

// Stacked switch rows share one tray, split by a hairline:
.filter-switch-group {
    border-radius: 12px;
    background: var(--body-light-muted-color);

    .filter-switch-row {
        border-radius: 0;
        background: transparent;
    }
    .filter-switch-row + .filter-switch-row {
        border-top: 1px solid var(--card-row-separator);
    }
}

.filter-switch {
    position: relative;
    flex-shrink: 0;
    width: 40px;
    height: 24px;
    border-radius: 12px;
    background: color-mix(in srgb, var(--body-muted-text-color) 40%, transparent);
    transition: background-color .2s ease;

    &::after {
        content: "";
        position: absolute;
        top: 2px;
        left: 2px;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #fff;
        box-shadow: 0 1px 3px rgba(0, 0, 0, .25);
        transition: transform .2s cubic-bezier(.2, .8, .2, 1);
    }
    &--on {
        background: var(--blue-bright);
        &::after {
            transform: translateX(16px);
        }
    }
}

.filter-close-btn {
    display: none;
    width: 100%;
    height: 44px;
    margin-top: 16px;
    border: none;
    border-radius: 16px;
    background: var(--body-light-muted-color);
    color: var(--body-text-color);
    font: inherit;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;

    &:active {
        opacity: .8;
    }
}

@keyframes filter-menu-in {
    from {
        opacity: 0;
        transform: translateY(-4px) scale(.98);
    }
}

@keyframes filter-sheet-in {
    from {
        transform: translateY(calc(100% + 8px + env(safe-area-inset-bottom)));
    }
}

@media screen and (max-width: 599px) {
    // A floating sheet: inset from the screen edges and rounded on all corners.
    .filter-menu {
        position: fixed;
        top: auto;
        bottom: calc(8px + env(safe-area-inset-bottom));
        left: 8px;
        right: 8px;
        width: auto;
        max-height: calc(100dvh - 100px);
        border-radius: 32px;
        overflow-y: auto;
        overscroll-behavior: none;
        transition: transform .3s cubic-bezier(.2, .8, .2, 1);
        animation: filter-sheet-in .3s cubic-bezier(.2, .8, .2, 1);

        &__card {
            padding: 12px 16px 16px;
            border-radius: 32px !important;
        }
        &__grabber-container {
            display: flex;
            margin-top: 0;
        }
    }
    .filter-chips__chip {
        height: 32px;
    }
    .filter-close-btn {
        display: block;
    }
}

@media (prefers-reduced-motion: reduce) {
    .filter-menu {
        animation: none;
    }
}
</style>
